import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";
import BackdropLoader from "../../components/BackdropLoader";
import Tabs from "../../components/Tabs";
import { AddGroup } from "../../features/recipients/AddGroup";
import BlackList from "../../features/recipients/BlackList";
import Group from "../../features/recipients/Group";
import Import from "../../features/recipients/Import";
import {
	useEditGroupNameMutation,
	useGetCurrentUserMailingGroupsQuery,
} from "../../features/recipients/recipientsApiSlice";
import RecipientsSkeleton from "../../features/recipients/Skeleton";

const Recipients = () => {
	const { t, i18n } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const {
		data,
		isLoading: isLoadingGroups,
		isFetching: isFetchingGroups,
	} = useGetCurrentUserMailingGroupsQuery();
	const [changeName, { isLoading }] = useEditGroupNameMutation();

	const handleGroupNameChange = useCallback(
		async (e, id) => {
			const newName = e.target.value;
			await changeName({ groupId: id, name: newName })
				.unwrap()
				.then(() => {
					enqueueSnackbar(t("updated_group_name"), {
						variant: "success",
					});
				})
				.catch((error) => {
					if (error?.data?.errors) {
						const errors = error.data.errors;
						Object.entries(errors).forEach(([_key, message]) => {
							enqueueSnackbar(message[0], {
								variant: "error",
								preventDuplicate: true,
							});
						});
					} else {
						enqueueSnackbar(error?.data?.message || t("default_error"), {
							variant: "error",
							preventDuplicate: true,
						});
					}
				});
		},
		[changeName, enqueueSnackbar]
	);

	const preparedGroups = useMemo(() => {
		return data?.groups
			? Object.entries(data.groups).map(([id, name]) => {
					return {
						label: name,
						content: (
							<Group
								groupId={id}
								onNameChange={(e) => handleGroupNameChange(e, id)}
								isChangingName={isLoading}
							/>
						),
					};
			  })
			: [];
	}, [data, handleGroupNameChange, isLoading]);

	const tabs = useMemo(() => {
		return [
			{
				label: t("import"),
				content: <Import mailingGroups={data?.groups} />,
			},
			...preparedGroups,
			{
				label: t("blacklist"),
				content: <BlackList />,
			},
		];
	}, [preparedGroups, data, i18n.language]);

	if (isLoadingGroups || !data.groups) {
		return <RecipientsSkeleton />;
	}

	return (
		<>
			<Box sx={{ maxWidth: "calc(100vw - 32px - 32px - 52px)" }}>
				<Tabs
					ariaLabel={t("recipients")}
					variant="scrollable"
					tabs={tabs}
					TabActionButton={<AddGroup groups={data?.groups} />}
				/>
			</Box>
			<BackdropLoader isOpen={isFetchingGroups} />
		</>
	);
};

export default Recipients;
